import React, { useState } from 'react';
import Card from '../components/ProfileCard.tsx';
import Button from '../components/Button';
import Badge from '../components/Badge';
import SearchInput from '../components/SearchInput';
import Pagination from '../components/Pagination';
import { Filter,  Calendar, MessageSquare } from 'lucide-react';

const ContactForm: React.FC = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [filterStatus, setFilterStatus] = useState('All');
    const [expandedContact, setExpandedContact] = useState<number | null>(null);
    const [replyText, setReplyText] = useState('');

    interface Contact {
        id: number;
        name: string;
        email: string;
        subject: string;
        message: string;
        createdAt: string;
        status: 'New' | 'In Progress' | 'Resolved';
        replies: {
            id: number;
            content: string;
            createdAt: string;
        }[];
    }

    // Sample data
    const [contacts, setContacts] = useState<Contact[]>([
        {
            id: 1,
            name: 'John Smith',
            email: 'john.smith@example.com',
            subject: 'Question about E-commerce Pro template',
            message: 'I\'m interested in purchasing the E-commerce Pro template, but I have a few questions before I make the decision. Does it support multiple languages? And is it compatible with the latest version of WordPress?',
            createdAt: '2025-03-01',
            status: 'Resolved',
            replies: [
                {
                    id: 1,
                    content: 'Hi John, thank you for your interest in our E-commerce Pro template. Yes, it supports multiple languages through WPML plugin integration, and it is fully compatible with the latest version of WordPress (6.3). Let me know if you have any other questions!',
                    createdAt: '2025-03-02',
                },
                {
                    id: 2,
                    content: 'Thank you for the quick response! That\'s exactly what I needed to know. I\'ll go ahead with the purchase.',
                    createdAt: '2025-03-03',
                },
            ],
        },
        {
            id: 2,
            name: 'Sarah Johnson',
            email: 'sarah.johnson@example.com',
            subject: 'Custom development request',
            message: 'I recently purchased your Portfolio Plus template and I love it. However, I need some custom features added to it for my specific needs. Do you offer custom development services, and if so, what are your rates?',
            createdAt: '2025-03-05',
            status: 'In Progress',
            replies: [
                {
                    id: 3,
                    content: 'Hello Sarah, we do offer custom development services for our templates. Our rates start at $50/hour, but we would need more details about the specific features you need to provide an accurate estimate. Could you please provide more information about what you\'re looking for?',
                    createdAt: '2025-03-06',
                },
            ],
        },
        {
            id: 3,
            name: 'Michael Brown',
            email: 'michael.brown@example.com',
            subject: 'Issue with Blog Master template',
            message: 'I\'m having an issue with the Blog Master template. The social sharing buttons are not working correctly. When I click on them, nothing happens. I\'ve tried clearing my cache and using different browsers, but the issue persists.',
            createdAt: '2025-03-08',
            status: 'New',
            replies: [],
        },
        {
            id: 4,
            name: 'Emily Davis',
            email: 'emily.davis@example.com',
            subject: 'Refund request',
            message: 'I purchased the Landing Page Pro template yesterday, but it doesn\'t have the features that were advertised. The page builder mentioned in the description is not included. I would like to request a refund.',
            createdAt: '2025-03-10',
            status: 'Resolved',
            replies: [
                {
                    id: 4,
                    content: 'Hi Emily, I\'m sorry to hear about your experience. You\'re right, and there seems to be a mistake in our description. I\'ve processed your refund, and it should appear in your account within 3-5 business days. We\'ll also update the template description to avoid any confusion in the future.',
                    createdAt: '2025-03-11',
                },
                {
                    id: 5,
                    content: 'Thank you for the quick resolution. I appreciate your understanding.',
                    createdAt: '2025-03-12',
                },
            ],
        },
        {
            id: 5,
            name: 'David Wilson',
            email: 'david.wilson@example.com',
            subject: 'Partnership inquiry',
            message: 'I run a web development agency, and we\'re looking for high-quality templates to offer to our clients. I\'m interested in discussing a potential partnership or reseller arrangement for your templates. Please let me know if this is something you\'d be open to.',
            createdAt: '2025-03-15',
            status: 'New',
            replies: [],
        },
    ]);

    // Filter and search
    const filteredContacts = contacts.filter((contact) => {
        const matchesSearch =
            contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            contact.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            contact.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
            contact.message.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesFilter = filterStatus === 'All' || contact.status === filterStatus;

        return matchesSearch && matchesFilter;
    });

    // Pagination
    const itemsPerPage = 5;
    const totalPages = Math.ceil(filteredContacts.length / itemsPerPage);
    const paginatedContacts = filteredContacts.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const handleReply = (contactId: number) => {
        if (replyText.trim() === '') return;

        const newReply = {
            id: Math.max(0, ...contacts.flatMap(c => c.replies.map(r => r.id))) + 1,
            content: replyText,
            createdAt: new Date().toISOString().split('T')[0],
        };

        setContacts(contacts.map(contact =>
            contact.id === contactId
                ? {
                    ...contact,
                    replies: [...contact.replies, newReply],
                    status: contact.status === 'New' ? 'In Progress' : contact.status,
                }
                : contact
        ));

        setReplyText('');
    };

    const handleStatusChange = (contactId: number, status: 'New' | 'In Progress' | 'Resolved') => {
        setContacts(contacts.map(contact =>
            contact.id === contactId
                ? { ...contact, status }
                : contact
        ));
    };

    const statusColors = {
        'New': 'warning',
        'In Progress': 'info',
        'Resolved': 'success',
    } as const;

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-bold text-gray-900">Contact Form Submissions</h1>
                <p className="mt-1 text-sm text-gray-500">
                    Manage and respond to contact form submissions
                </p>
            </div>

            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <SearchInput
                    placeholder="Search contacts..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="md:w-64"
                />

                <div className="flex items-center space-x-2">
                    <Filter size={20} className="text-gray-400" />
                    <select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                    >
                        <option value="All">All Statuses</option>
                        <option value="New">New</option>
                        <option value="In Progress">In Progress</option>
                        <option value="Resolved">Resolved</option>
                    </select>
                </div>
            </div>

            <div className="space-y-4">
                {paginatedContacts.length > 0 ? (
                    paginatedContacts.map((contact) => (
                        <Card key={contact.id} className="hover:shadow-md transition-shadow">
                            <div
                                className="flex flex-col space-y-4"
                                onClick={() => setExpandedContact(expandedContact === contact.id ? null : contact.id)}
                            >
                                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                                    <div className="flex-1">
                                        <div className="flex items-center">
                                            <h3 className="text-lg font-medium text-gray-900">{contact.subject}</h3>
                                            <Badge
                                                variant={statusColors[contact.status]}
                                                className="ml-2"
                                            >
                                                {contact.status}
                                            </Badge>
                                        </div>
                                        <p className="mt-1 text-sm text-gray-500">
                                            From: {contact.name} ({contact.email})
                                        </p>
                                        <div className="mt-2 flex items-center text-sm text-gray-500">
                                            <Calendar size={14} className="mr-1" />
                                            <span>Received: {contact.createdAt}</span>
                                            <span className="mx-2">•</span>
                                            <MessageSquare size={14} className="mr-1" />
                                            <span>{contact.replies.length} replies</span>
                                        </div>
                                    </div>

                                    <div className="flex items-center space-x-2">
                                        <Button
                                            variant={expandedContact === contact.id ? "primary" : "secondary"}
                                            size="sm"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setExpandedContact(expandedContact === contact.id ? null : contact.id);
                                            }}
                                        >
                                            {expandedContact === contact.id ? "Hide Details" : "View Details"}
                                        </Button>

                                        {contact.status !== 'Resolved' && (
                                            <Button
                                                variant="success"
                                                size="sm"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleStatusChange(contact.id, 'Resolved');
                                                }}
                                            >
                                                Mark Resolved
                                            </Button>
                                        )}
                                    </div>
                                </div>

                                {expandedContact === contact.id && (
                                    <div className="mt-4 space-y-4">
                                        <div className="bg-gray-50 p-4 rounded-md">
                                            <h4 className="text-sm font-medium text-gray-900">Message:</h4>
                                            <p className="mt-2 text-sm text-gray-700">{contact.message}</p>
                                        </div>

                                        {contact.replies.length > 0 && (
                                            <div className="space-y-4">
                                                <h4 className="text-sm font-medium text-gray-900">Conversation:</h4>
                                                {contact.replies.map((reply, index) => (
                                                    <div
                                                        key={reply.id}
                                                        className={`p-4 rounded-md ${
                                                            index % 2 === 0
                                                                ? 'bg-blue-50 ml-4'
                                                                : 'bg-gray-50 mr-4'
                                                        }`}
                                                    >
                                                        <div className="flex justify-between items-start">
                              <span className="text-sm font-medium text-gray-900">
                                {index % 2 === 0 ? 'Support Team' : contact.name}
                              </span>
                                                            <span className="text-xs text-gray-500">
                                {reply.createdAt}
                              </span>
                                                        </div>
                                                        <p className="mt-2 text-sm text-gray-700">{reply.content}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {contact.status !== 'Resolved' && (
                                            <div className="mt-4">
                                                <h4 className="text-sm font-medium text-gray-900">Reply:</h4>
                                                <div className="mt-2">
                          <textarea
                              rows={4}
                              className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                              placeholder="Type your response here..."
                              value={replyText}
                              onChange={(e) => setReplyText(e.target.value)}
                          />
                                                </div>
                                                <div className="mt-2 flex justify-end">
                                                    <Button
                                                        variant="primary"
                                                        onClick={() => handleReply(contact.id)}
                                                        disabled={!replyText.trim()}
                                                    >
                                                        Send Response
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </Card>
                    ))
                ) : (
                    <Card>
                        <div className="text-center py-6">
                            <p className="text-gray-500">No contact submissions found.</p>
                        </div>
                    </Card>
                )}
            </div>

            {filteredContacts.length > itemsPerPage && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                />
            )}
        </div>
    );
};

export default ContactForm;